import {
  DIMENSIONS,
  Dimension,
  FRAME_RULES_V1,
  MAX_BUDGET_MINOR,
  parseReferenceSize,
  type ReferenceSizeProblem,
} from '@optiflow/frame-recommendation';
import {
  FrameMaterial,
  FrameRimType,
  FrameShape,
  RecommendationDimensionMode,
  RecommendationPreferenceMode,
  TargetGroup,
} from '@optiflow/types';

/**
 * What an optician asks a recommendation for, and how it travels.
 *
 * Three shapes of the same thing: the form's draft (text, as typed), the criteria (validated, money in minor units and sizes in whole millimetres), and the request the API reads. Only the size shortcut parser is borrowed from the frozen rules package, so the screen accepts exactly the sizes the server does; nothing here scores, filters or orders a frame.
 */

/** The most values the API accepts in one list. */
export const MAX_LIST_VALUES = 20;
export const MAX_COLOUR_LENGTH = 80;
/** A card grid of 1, 2, 3 or 4 columns stays full at 24, and the API allows up to 100. */
export const RECOMMENDATION_PAGE_SIZE = 24;

export const FRAME_SHAPE_CHOICES = Object.values(FrameShape);
export const MATERIAL_CHOICES = Object.values(FrameMaterial);
export const RIM_TYPE_CHOICES = Object.values(FrameRimType);
export const TARGET_GROUP_CHOICES = Object.values(TargetGroup);

export interface MmRange {
  minMm: number | null;
  maxMm: number | null;
}

export interface RecommendationCriteria {
  budgetMinMinor: number | null;
  budgetMaxMinor: number | null;
  frameShapes: FrameShape[];
  frameShapesMode: RecommendationPreferenceMode;
  brandIds: string[];
  brandIdsMode: RecommendationPreferenceMode;
  colours: string[];
  coloursMode: RecommendationPreferenceMode;
  sizeShortcut: string | null;
  ranges: Record<Dimension, MmRange>;
  dimensionMode: RecommendationDimensionMode;
  materials: FrameMaterial[];
  rimTypes: FrameRimType[];
  targetGroups: TargetGroup[];
}

/** The form as typed: money and sizes stay text until the optician applies them. */
export interface CriteriaDraft {
  budgetMin: string;
  budgetMax: string;
  frameShapes: FrameShape[];
  frameShapesMode: RecommendationPreferenceMode;
  brandIds: string[];
  brandIdsMode: RecommendationPreferenceMode;
  colours: string[];
  /** A colour typed but not yet added; applying the form adds it rather than dropping it. */
  colourInput: string;
  coloursMode: RecommendationPreferenceMode;
  sizeShortcut: string;
  ranges: Record<Dimension, { min: string; max: string }>;
  dimensionMode: RecommendationDimensionMode;
  materials: FrameMaterial[];
  rimTypes: FrameRimType[];
  targetGroups: TargetGroup[];
}

/** A translation key and its values, so validation stays free of any language. */
export interface CriteriaError {
  key: string;
  values?: Record<string, string | number>;
}

export type CriteriaField = 'budgetMin' | 'budgetMax' | 'sizeShortcut' | `range.${Dimension}`;
export type CriteriaErrors = Partial<Record<CriteriaField, CriteriaError>>;

const emptyRanges = (): Record<Dimension, MmRange> => ({
  [Dimension.LENS_WIDTH]: { minMm: null, maxMm: null },
  [Dimension.BRIDGE_WIDTH]: { minMm: null, maxMm: null },
  [Dimension.TEMPLE_LENGTH]: { minMm: null, maxMm: null },
});

export const EMPTY_CRITERIA: RecommendationCriteria = {
  budgetMinMinor: null,
  budgetMaxMinor: null,
  frameShapes: [],
  frameShapesMode: RecommendationPreferenceMode.PREFER,
  brandIds: [],
  brandIdsMode: RecommendationPreferenceMode.PREFER,
  colours: [],
  coloursMode: RecommendationPreferenceMode.PREFER,
  sizeShortcut: null,
  ranges: emptyRanges(),
  dimensionMode: RecommendationDimensionMode.RANK,
  materials: [],
  rimTypes: [],
  targetGroups: [],
};

const hasRange = (range: MmRange) => range.minMm !== null || range.maxMm !== null;

export const hasBudget = (criteria: RecommendationCriteria) =>
  criteria.budgetMinMinor !== null || criteria.budgetMaxMinor !== null;

export const hasSize = (criteria: RecommendationCriteria) =>
  criteria.sizeShortcut !== null || DIMENSIONS.some((d) => hasRange(criteria.ranges[d]));

/** How many groups of filters are in use, for the count on the Filters button. */
export function activeFilterCount(criteria: RecommendationCriteria): number {
  return [
    hasBudget(criteria),
    criteria.frameShapes.length > 0,
    criteria.brandIds.length > 0,
    criteria.colours.length > 0,
    hasSize(criteria),
    criteria.materials.length > 0,
    criteria.rimTypes.length > 0,
    criteria.targetGroups.length > 0,
  ].filter(Boolean).length;
}

/** The applied criteria back into editable text, for reopening the form. */
export function draftFrom(
  criteria: RecommendationCriteria,
  toInput: (minor: number | null) => string,
): CriteriaDraft {
  const text = (value: number | null) => (value === null ? '' : String(value));
  return {
    budgetMin: toInput(criteria.budgetMinMinor),
    budgetMax: toInput(criteria.budgetMaxMinor),
    frameShapes: [...criteria.frameShapes],
    frameShapesMode: criteria.frameShapesMode,
    brandIds: [...criteria.brandIds],
    brandIdsMode: criteria.brandIdsMode,
    colours: [...criteria.colours],
    colourInput: '',
    coloursMode: criteria.coloursMode,
    sizeShortcut: criteria.sizeShortcut ?? '',
    ranges: {
      [Dimension.LENS_WIDTH]: {
        min: text(criteria.ranges[Dimension.LENS_WIDTH].minMm),
        max: text(criteria.ranges[Dimension.LENS_WIDTH].maxMm),
      },
      [Dimension.BRIDGE_WIDTH]: {
        min: text(criteria.ranges[Dimension.BRIDGE_WIDTH].minMm),
        max: text(criteria.ranges[Dimension.BRIDGE_WIDTH].maxMm),
      },
      [Dimension.TEMPLE_LENGTH]: {
        min: text(criteria.ranges[Dimension.TEMPLE_LENGTH].minMm),
        max: text(criteria.ranges[Dimension.TEMPLE_LENGTH].maxMm),
      },
    },
    dimensionMode: criteria.dimensionMode,
    materials: [...criteria.materials],
    rimTypes: [...criteria.rimTypes],
    targetGroups: [...criteria.targetGroups],
  };
}

const SIZE_PROBLEM_KEYS: Record<ReferenceSizeProblem, string> = {
  EMPTY: 'frameRecommendations.filters.errors.size.NOT_A_SIZE',
  NOT_A_SIZE: 'frameRecommendations.filters.errors.size.NOT_A_SIZE',
  MIXED_DIGITS: 'frameRecommendations.filters.errors.size.MIXED_DIGITS',
  TOO_MANY_PARTS: 'frameRecommendations.filters.errors.size.TOO_MANY_PARTS',
  OUT_OF_BOUNDS: 'frameRecommendations.filters.errors.size.OUT_OF_BOUNDS',
};

const WHOLE_MM = /^[0-9]{1,3}$/;

/** Colours differing only in letter case are one colour, as the rules compare them. */
export const sameColourListed = (colours: readonly string[], colour: string) =>
  colours.some((existing) => existing.toLocaleLowerCase() === colour.toLocaleLowerCase());

function budgetAmount(
  text: string,
  parseMoney: (value: string) => number | null,
): { minor: number | null; error?: CriteriaError } {
  if (text.trim() === '') return { minor: null };
  const minor = parseMoney(text);
  if (minor === null || minor < 0 || minor > MAX_BUDGET_MINOR) {
    return { minor: null, error: { key: 'frameRecommendations.filters.errors.amount' } };
  }
  return { minor };
}

/** Validates the draft into criteria the API will accept, or explains each field it cannot read. */
export function criteriaFrom(
  draft: CriteriaDraft,
  parseMoney: (value: string) => number | null,
): { criteria: RecommendationCriteria; errors: null } | { criteria: null; errors: CriteriaErrors } {
  const errors: CriteriaErrors = {};

  const min = budgetAmount(draft.budgetMin, parseMoney);
  const max = budgetAmount(draft.budgetMax, parseMoney);
  if (min.error) errors.budgetMin = min.error;
  if (max.error) errors.budgetMax = max.error;
  if (min.minor !== null && max.minor !== null && min.minor > max.minor) {
    errors.budgetMax = { key: 'frameRecommendations.filters.errors.budgetOrder' };
  }

  const shortcut = draft.sizeShortcut.trim();
  if (shortcut !== '') {
    const parsed = parseReferenceSize(shortcut, FRAME_RULES_V1);
    if (!parsed.ok) {
      const bounds = FRAME_RULES_V1.dimensions.boundsMm;
      errors.sizeShortcut = {
        key: SIZE_PROBLEM_KEYS[parsed.problem],
        values: {
          lensMin: bounds[Dimension.LENS_WIDTH].minMm,
          lensMax: bounds[Dimension.LENS_WIDTH].maxMm,
          bridgeMin: bounds[Dimension.BRIDGE_WIDTH].minMm,
          bridgeMax: bounds[Dimension.BRIDGE_WIDTH].maxMm,
          templeMin: bounds[Dimension.TEMPLE_LENGTH].minMm,
          templeMax: bounds[Dimension.TEMPLE_LENGTH].maxMm,
        },
      };
    }
  }

  const ranges = emptyRanges();
  for (const dimension of DIMENSIONS) {
    const bounds = FRAME_RULES_V1.dimensions.boundsMm[dimension];
    const typed = draft.ranges[dimension];
    const read = (value: string): number | null | 'invalid' => {
      const trimmed = value.trim();
      if (trimmed === '') return null;
      if (!WHOLE_MM.test(trimmed)) return 'invalid';
      const mm = Number.parseInt(trimmed, 10);
      return mm < bounds.minMm || mm > bounds.maxMm ? 'invalid' : mm;
    };
    const from = read(typed.min);
    const to = read(typed.max);
    if (from === 'invalid' || to === 'invalid') {
      errors[`range.${dimension}`] = {
        key: 'frameRecommendations.filters.errors.dimension',
        values: { min: bounds.minMm, max: bounds.maxMm },
      };
    } else if (from !== null && to !== null && from > to) {
      errors[`range.${dimension}`] = { key: 'frameRecommendations.filters.errors.dimensionOrder' };
    } else {
      ranges[dimension] = { minMm: from, maxMm: to };
    }
  }

  if (Object.keys(errors).length > 0) return { criteria: null, errors };

  const pending = draft.colourInput.trim().slice(0, MAX_COLOUR_LENGTH);
  const colours =
    pending !== '' && !sameColourListed(draft.colours, pending)
      ? [...draft.colours, pending]
      : draft.colours;

  return {
    criteria: {
      budgetMinMinor: min.minor,
      budgetMaxMinor: max.minor,
      frameShapes: draft.frameShapes.slice(0, MAX_LIST_VALUES),
      frameShapesMode: draft.frameShapesMode,
      brandIds: draft.brandIds.slice(0, MAX_LIST_VALUES),
      brandIdsMode: draft.brandIdsMode,
      colours: colours.slice(0, MAX_LIST_VALUES),
      coloursMode: draft.coloursMode,
      sizeShortcut: shortcut === '' ? null : shortcut,
      ranges,
      dimensionMode: draft.dimensionMode,
      materials: draft.materials.slice(0, MAX_LIST_VALUES),
      rimTypes: draft.rimTypes.slice(0, MAX_LIST_VALUES),
      targetGroups: draft.targetGroups.slice(0, MAX_LIST_VALUES),
    },
    errors: null,
  };
}

const RANGE_PARAMS: Record<Dimension, [string, string]> = {
  [Dimension.LENS_WIDTH]: ['lensWidthMin', 'lensWidthMax'],
  [Dimension.BRIDGE_WIDTH]: ['bridgeWidthMin', 'bridgeWidthMax'],
  [Dimension.TEMPLE_LENGTH]: ['templeLengthMin', 'templeLengthMax'],
};

/**
 * The request as the API reads it.
 *
 * Every list is a repeated parameter — a colour is free text and may itself contain a comma — and a mode travels only with values it applies to. Tenant and branch are never here: the server takes them from the session and the branch header.
 */
export function recommendationParams(request: {
  customerId: string;
  criteria: RecommendationCriteria;
  includeOtherBranches: boolean;
  page: number;
  pageSize?: number;
}): URLSearchParams {
  const { criteria } = request;
  const params = new URLSearchParams();
  params.set('customerId', request.customerId);
  params.set('page', String(request.page));
  params.set('pageSize', String(request.pageSize ?? RECOMMENDATION_PAGE_SIZE));

  if (criteria.budgetMinMinor !== null)
    params.set('budgetMinMinor', String(criteria.budgetMinMinor));
  if (criteria.budgetMaxMinor !== null)
    params.set('budgetMaxMinor', String(criteria.budgetMaxMinor));

  const list = (name: string, values: readonly string[], mode?: RecommendationPreferenceMode) => {
    if (values.length === 0) return;
    for (const value of values) params.append(name, value);
    if (mode) params.set(`${name}Mode`, mode);
  };
  list('preferredFrameShapes', criteria.frameShapes, criteria.frameShapesMode);
  list('preferredBrandIds', criteria.brandIds, criteria.brandIdsMode);
  list('preferredColours', criteria.colours, criteria.coloursMode);

  if (hasSize(criteria)) {
    if (criteria.sizeShortcut !== null) params.set('sizeShortcut', criteria.sizeShortcut);
    for (const dimension of DIMENSIONS) {
      const [minName, maxName] = RANGE_PARAMS[dimension];
      const range = criteria.ranges[dimension];
      if (range.minMm !== null) params.set(minName, String(range.minMm));
      if (range.maxMm !== null) params.set(maxName, String(range.maxMm));
    }
    params.set('dimensionMode', criteria.dimensionMode);
  }

  list('materials', criteria.materials);
  list('rimTypes', criteria.rimTypes);
  list('targetGroups', criteria.targetGroups);

  if (request.includeOtherBranches) params.set('includeOtherBranches', 'true');
  return params;
}

/** A filter group an optician can remove on its own from the applied filters. */
export type CriteriaGroup =
  | 'budget'
  | 'frameShapes'
  | 'brandIds'
  | 'colours'
  | 'size'
  | 'materials'
  | 'rimTypes'
  | 'targetGroups';

export function withoutGroup(
  criteria: RecommendationCriteria,
  group: CriteriaGroup,
): RecommendationCriteria {
  switch (group) {
    case 'budget':
      return { ...criteria, budgetMinMinor: null, budgetMaxMinor: null };
    case 'size':
      return {
        ...criteria,
        sizeShortcut: null,
        ranges: emptyRanges(),
        dimensionMode: RecommendationDimensionMode.RANK,
      };
    case 'frameShapes':
      return { ...criteria, frameShapes: [], frameShapesMode: RecommendationPreferenceMode.PREFER };
    case 'brandIds':
      return { ...criteria, brandIds: [], brandIdsMode: RecommendationPreferenceMode.PREFER };
    case 'colours':
      return { ...criteria, colours: [], coloursMode: RecommendationPreferenceMode.PREFER };
    case 'materials':
      return { ...criteria, materials: [] };
    case 'rimTypes':
      return { ...criteria, rimTypes: [] };
    case 'targetGroups':
      return { ...criteria, targetGroups: [] };
  }
}

// ===== Where the applied criteria live between visits to the frame details =====

/** Applied criteria, the branch scope and the page: the screen's state, kept in its own history entry. */
export interface AppliedRecommendation {
  criteria: RecommendationCriteria;
  includeOtherBranches: boolean;
  page: number;
}

export const INITIAL_APPLIED: AppliedRecommendation = {
  criteria: EMPTY_CRITERIA,
  includeOtherBranches: false,
  page: 1,
};

const STATE_KEY = 'frameRecommendations';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const oneOf = <T extends string>(allowed: readonly T[], value: unknown, fallback: T): T =>
  typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;

const listOf = <T extends string>(allowed: readonly T[] | null, value: unknown): T[] =>
  Array.isArray(value)
    ? (value.filter(
        (entry): entry is T =>
          typeof entry === 'string' &&
          (allowed === null || (allowed as readonly string[]).includes(entry)),
      ) as T[])
    : [];

const wholeOrNull = (value: unknown, max: number): number | null =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= max ? value : null;

/**
 * Reads the screen's state back from the history entry.
 *
 * The entry survives a visit to a frame's details and a reload, but it is still data from outside this render: anything unexpected falls back to no filters rather than reaching the request.
 */
export function readApplied(locationState: unknown): AppliedRecommendation {
  const stored = isRecord(locationState) ? locationState[STATE_KEY] : undefined;
  if (!isRecord(stored) || !isRecord(stored.criteria)) return INITIAL_APPLIED;

  const raw = stored.criteria;
  const rawRanges = isRecord(raw.ranges) ? raw.ranges : {};
  const ranges = emptyRanges();
  for (const dimension of DIMENSIONS) {
    const range = rawRanges[dimension];
    if (!isRecord(range)) continue;
    const bounds = FRAME_RULES_V1.dimensions.boundsMm[dimension];
    const inBounds = (value: unknown) => {
      const mm = wholeOrNull(value, bounds.maxMm);
      return mm !== null && mm >= bounds.minMm ? mm : null;
    };
    const minMm = inBounds(range.minMm);
    const maxMm = inBounds(range.maxMm);
    if (minMm === null || maxMm === null || minMm <= maxMm) ranges[dimension] = { minMm, maxMm };
  }
  const preferenceModes = Object.values(RecommendationPreferenceMode);

  return {
    criteria: {
      budgetMinMinor: wholeOrNull(raw.budgetMinMinor, MAX_BUDGET_MINOR),
      budgetMaxMinor: wholeOrNull(raw.budgetMaxMinor, MAX_BUDGET_MINOR),
      frameShapes: listOf(FRAME_SHAPE_CHOICES, raw.frameShapes).slice(0, MAX_LIST_VALUES),
      frameShapesMode: oneOf(preferenceModes, raw.frameShapesMode, 'PREFER'),
      brandIds: listOf(null, raw.brandIds).slice(0, MAX_LIST_VALUES),
      brandIdsMode: oneOf(preferenceModes, raw.brandIdsMode, 'PREFER'),
      colours: listOf(null, raw.colours)
        .filter((colour) => colour.length <= MAX_COLOUR_LENGTH)
        .slice(0, MAX_LIST_VALUES),
      coloursMode: oneOf(preferenceModes, raw.coloursMode, 'PREFER'),
      sizeShortcut:
        typeof raw.sizeShortcut === 'string' &&
        parseReferenceSize(raw.sizeShortcut, FRAME_RULES_V1).ok
          ? raw.sizeShortcut
          : null,
      ranges,
      dimensionMode: oneOf(Object.values(RecommendationDimensionMode), raw.dimensionMode, 'RANK'),
      materials: listOf(MATERIAL_CHOICES, raw.materials).slice(0, MAX_LIST_VALUES),
      rimTypes: listOf(RIM_TYPE_CHOICES, raw.rimTypes).slice(0, MAX_LIST_VALUES),
      targetGroups: listOf(TARGET_GROUP_CHOICES, raw.targetGroups).slice(0, MAX_LIST_VALUES),
    },
    includeOtherBranches: stored.includeOtherBranches === true,
    page:
      typeof stored.page === 'number' && Number.isInteger(stored.page) && stored.page >= 1
        ? stored.page
        : 1,
  };
}

/** The history entry's state with this screen's part replaced, leaving anything else in it alone. */
export function writeApplied(locationState: unknown, applied: AppliedRecommendation): unknown {
  return { ...(isRecord(locationState) ? locationState : {}), [STATE_KEY]: applied };
}
